import React, { useState, useEffect, useRef } from 'react';
import '@/App.css';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';

import FilterSection from '@/v2/pages/AcrossTheFloor/components/FilterSection';
import VideoGrid from '@/v2/pages/AcrossTheFloor/components/VideoGrid';
import MobileSearch from '@/v2/pages/AcrossTheFloor/components/MobileSearch';
import useVideoData from '@/v2/pages/AcrossTheFloor/hooks/useVideoData';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import checkApiStatus from '@/component/SubscripStatus';
import videoApi from '@/api/videoApi';
import api from '@/data/api';
import { useFetchProducts } from '@/data/swrs/shopify';
import AddToPlayListModal from '@/component/playlist/personal-playlist/AddToPlayListModal';
import { useAuth } from '@/v2/hooks/useAuth';
import NoVideosFoundSection from '../../components/NoVideosFoundSection';
import { useFilters } from '@/v2/hooks/useFilters';
import {getUniqueTagsFromVideos} from '@/utils/getUniqueTagsFromVideos';
import { getSignupRoute } from '@/constants/featureFlags';

export const AcrossTheFloor = () => {
	const navigate = useNavigate();
	const [videoData2, setVideoData2] = useState({});
	const [modal, setModal] = useState(false);
	const [videos, setVideos] = useState('');
	const { data } = useFetchProducts();
	const userData = useAtomValue(userInfoAtom);
	const categoryId = '6541ea30f1f99641d644a35d';

	// Custom hooks
	const { isLoggedIn } = useAuth();
	const {tags} = useFilters()
	const {
		responseData,
		setResponseData,
		totvideos,
		currentPage,
		startIndex,
		setCurrentPage,
		setStartIndex,
		selectedTags,
		setSelectedTags,
		selectedDurations,
		setSelectedDurations,
		selectedLevels,
		setselectedLevels,
		selectedInstructors,
		setSelectedInstructors,
		instructors,
		setInstructors,
		sortby,
		setSortBy,
		products,
		setProducts,
		selectedProducts,
		setSelectedProducts,
		fetchData,
		fetchVideosByTags,
		savedVideoIds,
		setSavedVideoIds
	} = useVideoData({ categoryId, userData, userInfo: userData.user, data });

	// Pagination handling
	const itemsPerPage = 15;
	const handlePageChange = newPage => {
		window.scrollTo(0, 0);
		if (newPage <= 0) {
			newPage = 1;
		}
		setCurrentPage(newPage);
		const newStartIndex = (newPage - 1) * itemsPerPage;
		setStartIndex(newStartIndex);
	};

	// Add to playlist
	const handleSubmitPlaylist = async _id => {
		try {
			const response = await api.videoPlayerPage(_id, userData.token);
			if (response.status === 200) {
				const jsonData = response.data[0];
				setVideoData2(jsonData);
				setModal(true);
			}
		} catch (error) {
			setModal(false);
			toast.error('Something went wrong', {
				position: 'top-center',
				duration: 3000
			});
		}
	};

	// Navigate to video player
	const handlePlayerVideo = async id => {
		const apiStatus = await checkApiStatus(userData.token);
		if (isLoggedIn) {
			if (apiStatus) {
				navigate(`/PlayerPage?v=${id}`);
			} else {
				navigate('/Plans');
			}
	} else {
			navigate(getSignupRoute());
		}
	};

	// Like/unlike video
	const handleLikeVideo = async (id, video) => {
		const isLiked =
			video.videoDetails?.savedvideo ||
			video.savedvideo ||
			(savedVideoIds?.length > 0 && savedVideoIds?.includes(video?.videoDetails?._id || video?._id));

		try {
			if (isLiked) {
				const response = await api.unsaveWishlist(id, userData?.token);
				if (response.status === 200) {
					updateVideoLikeStatus(id, false);
				}
			} else {
				const response = await api.saveWishlist(id, userData?.token);
				if (response.status === 200) {
					updateVideoLikeStatus(id, true);
				}
			}
		} catch (error) {
			console.error('Error updating like status:', error);
		}
	};

	// Helper function to update like status in state
	const updateVideoLikeStatus = (id, likeStatus) => {
		setResponseData(prevData => {
			return prevData.map(item => {
				const updatedVideos = item?.videos?.map(video => {
					if (video?.videoDetails?._id === id) {
						return {
							...video,
							videoDetails: {
								...video?.videoDetails,
								savedvideo: likeStatus
							}
						};
					} else if (video?._id === id) {
						return {
							...video,
							savedvideo: likeStatus
						};
					}
					return video;
				});
				return {
					...item,
					videos: updatedVideos
				};
			});
		});
	};

	// Shuffle videos
	const handleShuffle = categoryId => {
		setResponseData(prevData => {
			const shuffledData = [...prevData];
			const categoryIndex = shuffledData.findIndex(category => category?._id === categoryId);

			if (categoryIndex !== -1) {
				const shuffledVideos = shuffleArray([...shuffledData[categoryIndex].videos]);
				shuffledData[categoryIndex] = {
					...shuffledData[categoryIndex],
					videos: shuffledVideos
				};
			}

			return shuffledData;
		});
	};

	const shuffleArray = array => {
		const shuffledArray = [...array];
		for (let i = shuffledArray?.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
		}
		return shuffledArray;
	};

	// Fetch saved videos
	useEffect(() => {
		if (userData.token) {
			videoApi
				.getAllSavedVideoIds(userData.user.userId)
				.then(res => {
					setSavedVideoIds(res.data.savedVideos);
				})
				.catch(error => {
					console.error('Error fetching saved videos:', error);
				});
		}
	}, [userData.token, responseData]);

	// Filter and fetch videos when dependencies change
	useEffect(() => {
		fetchVideosByTags(selectedTags, selectedDurations, selectedLevels, selectedInstructors, selectedProducts);
	}, [
		currentPage,
		categoryId,
		startIndex,
		selectedTags,
		selectedDurations,
		selectedLevels,
		selectedInstructors,
		selectedProducts,
		sortby
	]);

	// Extract unique instructors from response data
	useEffect(() => {
		if (responseData?.length > 0 && instructors.length === 0) {
			setInstructors([
				...new Set(
					responseData[0]?.videos
						.filter(video => video.instructor !== undefined && video.instructor !== null)
						.map(video => video.instructor)
				)
			]);
		}
	}, [responseData, instructors.length]);
	
	const initialFocusRef = useRef([]);
		
	const uniqueTags = getUniqueTagsFromVideos(responseData[0]?.videos, initialFocusRef);

	return (
		<>
			{/* Mobile Search and Filter Section */}
			<MobileSearch
				tags={tags}
				sortby={sortby}
				handleFilterChange={e => setSortBy(e.target.value)}
				selectedTags={selectedTags}
				selectedDurations={selectedDurations}
				selectedLevels={selectedLevels}
				selectedInstructors={selectedInstructors}
				selectedProducts={selectedProducts}
				setSelectedTags={setSelectedTags}
				setSelectedDurations={setSelectedDurations}
				setselectedLevels={setselectedLevels}
				setSelectedInstructors={setSelectedInstructors}
				setSelectedProducts={setSelectedProducts}
				products={products}
				instructors={instructors}
			/>

			<div>
				<div className="tab-content fade-in" style={{ minHeight: '100vh' }}>
					<div id="menu2" className="tab-pane fade active show">
						{responseData?.map((category, index) => (
							<div key={index} id={category?.name?.split(' ')[0]}>
								{/* Desktop Filter Section */}
								<FilterSection
									tags={tags}
									category={category}
									sortby={sortby}
									setSortBy={setSortBy}
									selectedTags={selectedTags}
									selectedDurations={selectedDurations}
									selectedLevels={selectedLevels}
									selectedInstructors={selectedInstructors}
									selectedProducts={selectedProducts}
									setSelectedTags={setSelectedTags}
									setSelectedDurations={setSelectedDurations}
									setselectedLevels={setselectedLevels}
									setSelectedInstructors={setSelectedInstructors}
									setSelectedProducts={setSelectedProducts}
									instructors={instructors}
									products={products}
									handleShuffle={handleShuffle}
								/>
								{totvideos === 0 && <NoVideosFoundSection />}
								{/* Video Grid */}
								<VideoGrid
									category={category}
									isLoggedIn={isLoggedIn}
									handlePlayerVideo={handlePlayerVideo}
									handleLikeVideo={handleLikeVideo}
									handleSubmitPlaylist={handleSubmitPlaylist}
									selectedTags={selectedTags}
									setSelectedTags={setSelectedTags}
									savedVideoIds={savedVideoIds}
									totvideos={totvideos}
									currentPage={currentPage}
									handlePageChange={handlePageChange}
									modal={modal}
									setModal={setModal}
									setVideoData={setVideoData2}
									selectTagCallback={tag => {
										setSelectedTags(tags => {
											if (tags.includes(tag._id)) {
												return tags.filter(t => t !== tag._id);
											}
											return [...tags, tag._id];
										});
									}}
								/>
							</div>
						))}
					</div>
				</div>

				{/* Add To Playlist Modal */}
				<AddToPlayListModal modal={modal} setModal={setModal} activeObject={videoData2} viewData={videos} />
			</div>
		</>
	);
};
